import {useState} from "react";
import {FiPlus} from "react-icons/fi";
import {IoOptionsOutline} from "react-icons/io5";

import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {Dropdown} from "@/components/dropdown/Dropdown.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {ChecksListView} from "@/lib/types/checksListViews.ts";
import {DEFAULT_COLUMNS} from "@/pages/checks/list/columns/checksColumnSpecs.tsx";
import {useChecksListViewsContext} from "@/pages/checks/list/columns/ChecksListViewsContext.tsx";
import {ViewEditor} from "@/pages/checks/list/components/ViewEditor/ViewEditor.tsx";
import {ViewSelectorDropdownItem} from "@/pages/checks/list/components/ViewSelector/ViewSelectorDropdownItem.tsx";

import styles from "./ViewSelector.module.scss";

interface ViewSelectorProps {
  className?: string;
}

export const ViewSelector = ({className}: ViewSelectorProps) => {
  const {
    isEnabled,
    views,
    activeView,
    availableColumns,
    selectView: onSelectView,
    deleteView: onDeleteView,
  } = useChecksListViewsContext();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingView, setEditingView] = useState<ChecksListView | null>(null);
  const [deleteConfirmView, setDeleteConfirmView] = useState<ChecksListView | null>(null);

  const hasDefaultView = views.some((view) => view.is_default);

  const handleCreateNew = () => {
    setEditingView(null);
    setEditorOpen(true);
  };

  const handleEdit = (view: ChecksListView) => {
    setEditingView(view);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingView(null);
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <div className={className}>
        <Dropdown>
          <Dropdown.Toggle variant="ghost" size="square">
            <Icon icon={IoOptionsOutline} className="" />
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.dropdownMenu} portal>
            <div className={styles.viewOptionList}>
              <ViewSelectorDropdownItem
                name="Standard"
                columnCount={DEFAULT_COLUMNS.length}
                isActive={!activeView}
                isDefault={!hasDefaultView}
                onSelect={() => onSelectView(null)}
              />
              {views.map((view) => (
                <ViewSelectorDropdownItem
                  key={view.id}
                  name={view.name}
                  columnCount={view.columns.length}
                  isActive={activeView?.id === view.id}
                  isDefault={view.is_default}
                  onSelect={() => onSelectView(view.id)}
                  onEdit={() => handleEdit(view)}
                  onDelete={() => setDeleteConfirmView(view)}
                />
              ))}
            </div>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleCreateNew} className={styles.createViewDropdownItem}>
              <Icon icon={FiPlus} className="text-nowrap" />
              <span>Create Custom View</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <ViewEditor
        show={editorOpen}
        onClose={closeEditor}
        onSave={closeEditor}
        onDelete={editingView ? () => setDeleteConfirmView(editingView) : undefined}
        availableColumns={availableColumns}
        editingView={editingView}
      />

      <DeleteActionDialog
        show={!!deleteConfirmView}
        title="Delete View"
        onCancel={() => setDeleteConfirmView(null)}
        onConfirm={async () => {
          if (deleteConfirmView) {
            await onDeleteView(deleteConfirmView.id);
          }
          setDeleteConfirmView(null);
          closeEditor();
        }}
      >
        Are you sure you want to delete the view &ldquo;{deleteConfirmView?.name}&rdquo;?
      </DeleteActionDialog>
    </>
  );
};
